import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Funnel {
  id: string
  name: string
  total_visitors: number
  total_conversions: number
  conversion_rate: number
  steps: { name: string; visitors: number; dropoff_rate: number }[]
  revenue: number
}

interface Opportunity {
  id: string
  page: string
  opportunity_type: string
  potential_lift: number
  confidence: number
  priority: string
  current_metric: number
  estimated_impact: number
  recommendation: string
}

interface Statistics {
  total_funnels: number
  avg_conversion_rate: number
  total_opportunities: number
  high_priority_opportunities: number
  potential_revenue_increase: number
  total_visitors_analyzed: number
  avg_dropoff_rate: number
  optimization_score: number
}

export default function ConversionOptimizationPage() {
  const [funnels, setFunnels] = useState<Funnel[]>([])
  const [opportunities, setOpportunities] = useState<Opportunity[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'funnels' | 'opportunities'>('funnels')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [funnelsRes, oppRes, statsRes] = await Promise.all([
        fetch('/api/conversionoptimization/funnels'),
        fetch('/api/conversionoptimization/opportunities'),
        fetch('/api/conversionoptimization/statistics')
      ])

      if (funnelsRes.ok) setFunnels(await funnelsRes.json())
      if (oppRes.ok) setOpportunities(await oppRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching conversion optimization data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getPriorityBadge = (priority: string) => {
    const classes: Record<string, string> = {
      critical: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-green-100 text-green-800'
    }
    return classes[priority] || 'bg-gray-100 text-gray-800'
  }

  const getTypeBadge = (type: string) => {
    const classes: Record<string, string> = {
      form_optimization: 'bg-blue-100 text-blue-800',
      cta_improvement: 'bg-purple-100 text-purple-800',
      page_speed: 'bg-orange-100 text-orange-800',
      ux_improvement: 'bg-green-100 text-green-800',
      content_optimization: 'bg-pink-100 text-pink-800'
    }
    return classes[type] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Conversion Optimization</h1>
          <p className="mt-2 text-gray-600">Funnel analysis and conversion improvement opportunities</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Conversion Rate</p>
              <p className="text-2xl font-bold text-green-600">{(statistics.avg_conversion_rate * 100).toFixed(2)}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Opportunities</p>
              <p className="text-2xl font-bold text-blue-600">{statistics.total_opportunities}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Potential Revenue</p>
              <p className="text-2xl font-bold text-purple-600">${statistics.potential_revenue_increase.toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Optimization Score</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.optimization_score}/100</p>
            </div>
          </div>
        )}

        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('funnels')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'funnels' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Funnels ({funnels.length})
            </button>
            <button
              onClick={() => setActiveTab('opportunities')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'opportunities' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'
              }`}
            >
              Opportunities ({opportunities.length})
            </button>
          </nav>
        </div>

        {activeTab === 'funnels' && (
          <div className="space-y-6">
            {funnels.map((funnel) => (
              <div key={funnel.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start mb-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{funnel.name}</h3>
                    <p className="text-sm text-gray-500">{funnel.total_visitors.toLocaleString()} visitors</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">{(funnel.conversion_rate * 100).toFixed(2)}%</p>
                    <p className="text-sm text-gray-500">${funnel.revenue.toLocaleString()} revenue</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {funnel.steps.map((step, idx) => (
                    <div key={idx} className="flex-1">
                      <div className="flex items-center">
                        <div className="flex-1 h-12 bg-blue-100 rounded flex items-center justify-center relative">
                          <span className="text-sm font-medium text-blue-800">{step.visitors.toLocaleString()}</span>
                          {idx < funnel.steps.length - 1 && (
                            <div className="absolute -right-3 w-6 h-6 bg-red-100 rounded-full flex items-center justify-center z-10">
                              <span className="text-xs font-bold text-red-600">-{(step.dropoff_rate * 100).toFixed(0)}%</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 text-center mt-1">{step.name}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'opportunities' && (
          <div className="space-y-4">
            {opportunities.map((opp) => (
              <div key={opp.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{opp.page}</h3>
                      <span className={`px-2 py-1 text-xs rounded-full ${getPriorityBadge(opp.priority)}`}>
                        {opp.priority}
                      </span>
                      <span className={`px-2 py-1 text-xs rounded-full ${getTypeBadge(opp.opportunity_type)}`}>
                        {opp.opportunity_type.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{opp.recommendation}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-600">+{(opp.potential_lift * 100).toFixed(1)}%</p>
                    <p className="text-sm text-gray-500">${opp.estimated_impact.toLocaleString()} impact</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Confidence:</span>
                    <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full" style={{ width: `${opp.confidence * 100}%` }}></div>
                    </div>
                    <span className="text-sm font-medium">{(opp.confidence * 100).toFixed(0)}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
